<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	// Design system components
	import { IconButton, LabelListV2, Loader, Text } from '@Shelly/shelly-design-system';
	import { getPVSetupByType, type PVSetupTypes } from './pv_setup_helper';
	//Helpers
	import { getPVSetupChartsConfig, getPVSetupDevices } from './pv_setup_helper';
	import { generateCSVStatistic } from 'src/helpers/statistics/ui/chart_download_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	// Components
	import LayerChartRenderer from '../../Chart/LayerChart/LayerChartRenderer.svelte';
	import ConsumptionContainer from '../ConsumptionContainer.svelte';
	// Types
	import type { ChartConfig, ChartDataInterface } from 'src/types/Chart';
	import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';

	export let chartControlsData: ChartControlsDataInterface;
	export let pvSetupType: PVSetupTypes;

	const chartConfig: ChartConfig = getPVSetupChartsConfig();
	const title: string = capFirstChar(
		$format('label_pv_report', {
			default: 'PV',
		}),
	);

	const getStatsPeriod = async (
		data: ChartControlsDataInterface,
		pvSetupType: PVSetupTypes,
	): Promise<ChartDataInterface> => {
		if (data && pvSetupType) {
			const pvSetupDevices = getPVSetupDevices(pvSetupType);
			return getPVSetupByType(
				pvSetupDevices.gridDevices,
				pvSetupDevices.solarDevices,
				pvSetupType,
				chartConfig,
				data,
			);
		}
	};

	const getChartTitle = (pvSetupType: PVSetupTypes) => {
		const formatKey = `option_${chartControlsData.currentPeriod}`;
		return `${title} ${pvSetupType} - ${$format(formatKey)}`;
	};

	$: statsData = getStatsPeriod(chartControlsData, pvSetupType);
</script>

{#if statsData}
	<ConsumptionContainer noWrapperContainer>
		{#await statsData}
			{#if title}
				<div class="mb-s d-flex align-items-center">
					<Text class="text-bold width-full" style="margin-right:-32px">
						{getChartTitle(pvSetupType)}
					</Text>
					<IconButton
						variant="secondary"
						icon="far fa-file-arrow-down"
						disabled={true}
						aria-label={$format('button_download_chart_data', {
							default: 'Download chart data',
						})}
					/>
				</div>
			{/if}
			<div
				class="d-flex justify-content-center align-items-center"
				style={`height: ${chartConfig.height}px`}
			>
				<Loader size="m" />
			</div>
		{:then chartData}
			{#if chartData}
				{#if title}
					<div class="mb-s d-flex align-items-center justify-content-between">
						<Text class="text-bold width-full" noWrap style="text-indent:32px">
							{getChartTitle(pvSetupType)}
						</Text>
						<IconButton
							variant="secondary"
							icon="far fa-file-arrow-down"
							aria-label={$format('button_download_chart_data', {
								default: 'Download chart data',
							})}
							on:click={() => {
								generateCSVStatistic(chartData, title, chartControlsData.time);
							}}
						/>
					</div>
				{/if}
				<div class="d-flex justify-content-center mb-s">
					<LabelListV2
						a11yProps={{
							isInteractive: false,
						}}
						size="xl"
						labelList={chartData.totalConsumption.concat(
							chartData?.totalReturnedConsumption || [],
						)}
					/>
				</div>

				<LayerChartRenderer {chartData} />
			{/if}
		{:catch error}
			<div
				class="d-flex justify-content-center align-items-center"
				style={`height: ${chartConfig.height}px`}
			>
				<Text class="body-small-styles text-center mt-m mb-s ">
					<i class="fas fa-exclamation-triangle text-warning mr-xs"></i>
					{error.message}
				</Text>
			</div>
		{/await}
	</ConsumptionContainer>
{/if}
